import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Alumni, AlumniExtended, Roles } from "iasma-datatypes";
import { useAuth } from "@/contexts/AuthContext"
import { useNotify } from "@/contexts/NotifyContext/useNotify";
import { queryAlumni, queryAlumniList, queryNames } from "@/services/DirectoryService";
import { Profile } from "@/components/directories/profile/Profile";
import { useVersionCheck } from "@/contexts/VersionCheckContext";
import { Badge } from "../ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { ANGKATAN_PERTAMA, SELF_ENTRY_INDEX_THRESHOLD } from "iasma-datatypes/build/constants-sla";
import { Loading } from "../Loading";

type Breakpoint = "mobile" | "sm" | "md" | "lg";

interface ColumnDef {
	key: keyof Alumni;
	label: string;
	/** Smallest breakpoint at which this column becomes visible. */
	minBreakpoint: Breakpoint;
	/** If true, only admins ever see this column, at any breakpoint. */
	adminOnly?: boolean;
	/** Optional custom cell renderer. */
	render?: (row: Alumni) => React.ReactNode;
	/** Right/left align, mainly for numeric-ish columns. */
	align?: "left" | "right" | "center";
}

/**
 * ─────────────────────────────────────────────────────────────────────────
 * COLUMN CONFIG — the single source of truth
 * Add/remove/reorder columns here; visibility follows automatically.
 * ─────────────────────────────────────────────────────────────────────────
 */
const COLUMNS: ColumnDef[] = [
	{ key: "Name", label: "Nama", minBreakpoint: "mobile" },
	{ key: "Graduation", label: "Angkatan", minBreakpoint: "mobile" },

	// sm and up (admin only)
	{ key: "Classes", label: "Kelas", minBreakpoint: "sm", adminOnly: true },
	{
		key: "Status",
		label: "Status",
		minBreakpoint: "sm",
		adminOnly: true,
		render: (row) => <StatusBadge status={row.Status!} />,
	},

	// md and up (admin only)
	{ key: "Alias", label: "Nickname", minBreakpoint: "md", adminOnly: true },
	{ key: "NIS", label: "NIS", minBreakpoint: "md", adminOnly: true },
	//   { key: "pekerjaan", label: "Pekerjaan", minBreakpoint: "md", adminOnly: true },

	// lg and up (admin only)
	{ key: "Title", label: "Titel", minBreakpoint: "lg", adminOnly: true },
	{ key: "Phones", label: "Handphone", minBreakpoint: "lg", adminOnly: true },
	//   { key: "email", label: "Email", minBreakpoint: "lg", adminOnly: true },
	{ key: "BirthDate", label: "Lahir", minBreakpoint: "lg", adminOnly: true },
	// { key: "Addresses", label: "Alamat", minBreakpoint: "lg", adminOnly: true },
];

/** Tailwind visibility classes per breakpoint. `mobile` = always visible. */
const BREAKPOINT_CLASS: Record<Breakpoint, string> = {
	mobile: "table-cell",
	sm: "hidden sm:table-cell",
	md: "hidden md:table-cell",
	lg: "hidden lg:table-cell",
};

function StatusBadge({ status }: { status: number }) {
	let variant: string = "bg-zinc-100 text-zinc-500 hover:bg-zinc-100"
	switch (status) {
		case 0: variant = "bg-emerald-100 text-emerald-700 hover:bg-emerald-100"; break;
		case 1: variant = "bg-blue-100 text-blue-700 hover:bg-blue-100"; break;
		case 2: variant = "bg-amber-100 text-amber-700 hover:bg-amber-100"; break;
	}
	return <Badge className={`font-normal ${variant}`}>{status}</Badge>;
}

export const ViewDuplicate: React.FC = () => {
	const { session, setToken } = useAuth()
	const { setNotify } = useNotify();
	const { receiveVersion } = useVersionCheck();

	const [angkatan, setAngkatan] = useState<number>(session?.User?.Alumni?.Graduation!);
	const [isAdmin, setIsAdmin] = useState<boolean>(false);

	const [alumniList, setAlumniList] = useState<Alumni[]>([]);
	const [searchTerm, setSearchTerm] = useState('');
	const [filteredAlumniList, setFilteredAlumniList] = useState<Alumni[]>([]);

	const [currentAlumniId, setCurrentAlumniId] = useState(0);
	const [currentProfile, setCurrentProfile] = useState<AlumniExtended | undefined>(undefined);

	const years = Array.from({ length: 2025 - ANGKATAN_PERTAMA + 1 }, (_, i) => 2025 - i);

	const visibleColumns = useMemo(
		() => COLUMNS.filter((col) => isAdmin || !col.adminOnly),
		[isAdmin]
	);

	useEffect(() => {
		const checkAdmin = async () => {
			if (session!.Roles.indexOf(Roles.ADMIN_ALL) >= 0) {
				setIsAdmin(true);
			} else if (session!.Roles.indexOf(Roles.ADMIN_ANGKATAN) >= 0) {
				if (session!.User!.Alumni!.Graduation === angkatan) {
					setIsAdmin(true);
				}
			}
		}

		const fetchAlumniList = async () => {
			try {
				setToken(axios);
				const recs: Alumni[] = await queryAlumniList(angkatan);
				setAlumniList(recs);
			} catch (error: any) {
				setNotify({
					title: "Error Fetching AlumniList",
					error,
					severity: 'error',
				});
			}
		};

		setNotify(null);
		setAlumniList([]);

		if (angkatan >= ANGKATAN_PERTAMA) {
			checkAdmin();
			fetchAlumniList();
		} else {
			// all angkatan mulai dari kosong
			setFilteredAlumniList([]);
		}
	}, [angkatan]);

	useEffect(() => {
		// angkatan tertentu
		if (angkatan >= ANGKATAN_PERTAMA) {
			setFilteredAlumniList(
				alumniList.filter(alumnus =>
					alumnus.Name.toLowerCase().includes(searchTerm.toLowerCase())
					|| (alumnus.Title && alumnus.Title.toLowerCase().includes(searchTerm.toLowerCase()))
					|| (alumnus.Alias && alumnus.Alias.toLowerCase().includes(searchTerm.toLowerCase()))
					|| (alumnus.Classes && alumnus.Classes.toLowerCase().includes(searchTerm.toLowerCase()))
				)
			);
		}
	}, [alumniList, searchTerm]);

	useEffect(() => {
		const searchAlumnies = async () => {
			try {
				setToken(axios);
				const result = await queryNames(searchTerm);
				console.log(result);
				setAlumniList(result.rows);
			} catch (error: any) {
				setNotify({
					title: "Error Fetching Names",
					error,
					severity: 'error'
				});
			}
		};

		// semua angkatan
		if ((angkatan < ANGKATAN_PERTAMA) && (searchTerm.length >= 2)) {
			setFilteredAlumniList(alumniList);
			searchAlumnies();
		}

	}, [searchTerm]);

	useEffect(() => {
		const fetchAlumnus = async () => {
			try {
				setNotify(null);
				if (currentAlumniId) {
					setToken(axios);
					const result: AlumniExtended = await queryAlumni(currentAlumniId, receiveVersion);
					setCurrentProfile(result);
				}
			} catch (error: any) {
				setNotify({
					title: "Error Fetching Alumni",
					error,
					severity: 'error'
				});
			}
		};
		fetchAlumnus();
	}, [currentAlumniId, receiveVersion]);


	const handleCurrentAlumniClose = () => {
		setCurrentAlumniId(0)
		setCurrentProfile(undefined)
	}



	if (currentAlumniId > 0) {
		if (!currentProfile) {
			return <Loading />;
		} else {
			return <Profile profile={currentProfile} editable={isAdmin} onClose={handleCurrentAlumniClose} />
		}

	} else if ((alumniList.length === 0) && (angkatan >= ANGKATAN_PERTAMA)) {
		return <Loading />;
	} else {
		return (
			<div className="flex flex-col overflow-hidden bg-(--color-background) md:p-2 sm:p-1">
				<div className="p-2">
					<div className="grid md:grid-cols-2 gap-2 mb-2">
						<select
							value={angkatan}
							onChange={(e) => setAngkatan(Number(e.target.value))}
							className="px-4 py-2 border border-(--color-border) rounded-lg focus:outline-none focus:ring-2 focus:ring-(--color-ring)"
						>
							{years.map(year => (
								<option key={year} value={year}>Angkatan {year}</option>
							))}
							<option key={-1} value={-1}>Semua Angkatan</option>
						</select>
						<div className="relative">
							<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-(--color-text-secondary)" size={20} />
							<input
								type="text"
								placeholder={angkatan < ANGKATAN_PERTAMA ? "Cari Nama" : "nama, gelar, panggilan, kelas"}
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="w-full pl-10 pr-4 py-2 text-(--color-text) border border-(--color-border) rounded-lg focus:outline-none focus:ring-2 focus:ring-(--color-ring)"
							/>
						</div>
					</div>
				</div>
				<div className="overflow-auto">
					<div className="min-w-full max-h-[calc(100vh-280px)]">
						<Table>
							<TableHeader className="bg-(--color-surface) text-(--color-text) sticky top-0 z-50">
								<TableRow>
									{visibleColumns.map((col) => (
									<TableHead
										key={col.key}
										className={`${BREAKPOINT_CLASS[col.minBreakpoint]} whitespace-nowrap ${
											col.align === "right" ? "text-right" : ""
										}`}
									>
										{col.label}
									</TableHead>
									))}
								</TableRow>
							</TableHeader>
							<TableBody className="divide-y divide-gray-200">
								{filteredAlumniList.map((alumnus) => (
									<TableRow key={alumnus.Id} className={"hover:bg-(--color-surface-hover)" + (isAdmin && (alumnus.Id > SELF_ENTRY_INDEX_THRESHOLD) ? " bg-gray-200" : "")}>
									{visibleColumns.map((col) => (
										<TableCell
											key={col.key}
											className={`${BREAKPOINT_CLASS[col.minBreakpoint]} ${
											col.key === "Name" ? "font-medium text-zinc-900" : "text-zinc-600"
											} ${col.align === "right" ? "text-right" : ""}`}
										>
											{col.render ? col.render(alumnus) : String(alumnus[col.key])}
										</TableCell>
									))}

										{/* <td className="p-1 truncate text-sm items-end justify-end" onClick={() => handleCurrentSelected(alumnus)}>{alumnus.Name}</td>
										<td className="p-1 text-sm">
											{isAdmin && <div className="text-center flex gap-1 flex-row">
												{{(selectedIds.length >= 2 && alumnus.Id === selectedIds[1]) ? 
											<span onClick={handleMerge} className="cursor-pointer">merge</span> : <span>&nbsp;</span>}
											<input
												type="checkbox"
												checked={selectedIds.includes(alumnus.Id)}
												onChange={() => handleSelect(alumnus.Id)}
												disabled={selectedIds.length >= 2 && !selectedIds.includes(alumnus.Id)}
												className="text-blue-600 focus:ring-2 disabled:opacity-50"
											/>}
												<span>{alumnus.Graduation}</span>
											</div>}
										</td>
										<td className="p-1 text-nowrap">{alumnus.Title}</td>
										<td className="p-1 text-nowrap">{alumnus.Alias}</td>
										<td className="p-1 text-nowrap">{alumnus.Classes}</td>
										<td className="p-1">{alumnus.DOD ? 'Almarhum ' + alumnus.DOD : ''}</td>
										<td className="p-1">{alumnus.NIS}</td>
										<td className="p-1 text-nowrap">{alumnus.BirthDate ? '' + alumnus.BirthDate : ''}</td>
										<td className="p-1 min-w-80">{alumnus.Addresses?.length}</td>
										<td className="p-1">{alumnus.Businesses?.length}</td>
										<td className="p-1">{alumnus.Phones}</td>
										<td className="p-1">{alumnus.Status}</td>
										<td className="px-1">
											<input
												type="checkbox"
												checked={selectedIds.includes(alumnus.Id)}
												onChange={() => handleSelect(alumnus.Id)}
												disabled={selectedIds.length >= 2 && !selectedIds.includes(alumnus.Id)}
												className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
											/>
										</td> */}
										{/* <td className="p-1 bg-(--color-background)/85 sticky right-0 z-30">
											<div className="flex justify-center gap-2">
												<button
													onClick={() => handleView(alumnus)}
													className="hover:bg-blue-50 rounded-md transition"
												>
													<Eye size={18} />
												</button>
												<button
													onClick={() => handleEdit(alumnus)}
													className="hover:bg-blue-50 rounded-md transition"
												>
													<Edit2 size={18} />
												</button>
												<button
													onClick={() => handleDelete(alumnus)}
													className="text-red-600 hover:bg-red-50 rounded-md transition"
												>
													<Trash2 size={18} />
												</button> 
											</div>
										</td> */}
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				</div>
			</div>
		)
	}
}